import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EditingFrom, ModalType } from '../administrate-schedule-cycle-question-banks.component';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SurveyQuestionType } from '@app/models/survey-question-type.enum';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { EvaluationCycleQuestion } from '@app/models/evaluation/evaluation-cycle-question.model';
import { QuestionBankQuestion } from '@app/models/evaluation/question-bank-question.model';
import { DynamicScalePossibleAnswer } from '@app/models/evaluation/dynamic-scale-possible-answer.model';
import { FrankliValidators } from '@app/shared/validators/validators';

enum ComponentAction {
  CANCEL = 'CANCEL',
  SUBMIT = 'SUBMIT'
}

const DEFAULT_ANSWER_COLOR = '#CCCCCC';

const DEFAULT_ANSWER_OPTIONS: DynamicScalePossibleAnswer[] = [
  {
    id: null,
    answerIndex: 0,
    answerValue: '1',
    colour: DEFAULT_ANSWER_COLOR,
    labelTitle: 'Option 1',
    labelDescription: ''
  },
  {
    id: null,
    answerIndex: 1,
    answerValue: '2',
    colour: DEFAULT_ANSWER_COLOR,
    labelTitle: 'Option 2',
    labelDescription: ''
  },
  {
    id: null,
    answerIndex: 2,
    answerValue: '3',
    colour: DEFAULT_ANSWER_COLOR,
    labelTitle: 'Option 3',
    labelDescription: ''
  },
  {
    id: null,
    answerIndex: 3,
    answerValue: '4',
    colour: DEFAULT_ANSWER_COLOR,
    labelTitle: 'Option 4',
    labelDescription: ''
  },
  {
    id: null,
    answerIndex: 4,
    answerValue: '5',
    colour: DEFAULT_ANSWER_COLOR,
    labelTitle: 'Option 5',
    labelDescription: ''
  }
];

export const initFormAddReviewQuestion = (editingFrom?: EditingFrom) => {
  const formGroup = new UntypedFormGroup({
    text: new UntypedFormControl('', [Validators.required, Validators.maxLength(255)]),
    type: new UntypedFormControl(null, [Validators.required]),
    index: new UntypedFormControl(null, []),
    editingFrom: new UntypedFormControl(EditingFrom.CYCLE_LIST, []),
    options: initFormArrayOptionItems()
  });

  if (editingFrom) {
    formGroup.controls.editingFrom.setValue(editingFrom, { emitEvent: false } );
  }

  formGroup.controls.type.valueChanges.subscribe((type: SurveyQuestionType) => {
    switch (type) {
      case SurveyQuestionType.DYNAMIC_SCALE:
        formGroup.controls.options.setValidators([
          FrankliValidators.minLengthArray(2),
          FrankliValidators.formArrayControlValuesMustBeUnique('answerValue')
        ]);
        formGroup.controls.options.updateValueAndValidity();
        break;
      default:
        formGroup.controls.options.setValidators([]);
        formGroup.controls.options.updateValueAndValidity();
        break;
    }
  });

  return formGroup;
};

export const initFormAddReviewQuestionEC = (question: EvaluationCycleQuestion, editingFrom?: EditingFrom): UntypedFormGroup => {
  const formGroup = initFormAddReviewQuestion(editingFrom);

  if (question) {
    formGroup.controls.text.setValue(question.questionText, { emitEvent: false } );
    formGroup.controls.type.setValue(question.questionBankQuestion.surveyQuestionType, { emitEvent: false } );
    formGroup.controls.index.setValue(question.orderIndex, { emitEvent: false } );
    formGroup.controls.options = initFormArrayOptionItems(question.questionBankQuestion.possibleAnswers);
  }

  return formGroup;
};

export const initFormAddReviewQuestionQB = (question: QuestionBankQuestion, editingFrom?: EditingFrom): UntypedFormGroup => {
  const formGroup = initFormAddReviewQuestion(editingFrom);

  if (question) {
    formGroup.controls.text.setValue(question.questionText, { emitEvent: false } );
    formGroup.controls.type.setValue(question.surveyQuestionType, { emitEvent: false } );
    formGroup.controls.options = initFormArrayOptionItems(question.possibleAnswers);
  }

  return formGroup;
};

export const initFormGroupOptionItem = (possibleAnswer?: DynamicScalePossibleAnswer): UntypedFormGroup => {
  const formGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null, []),
    answerIndex: new UntypedFormControl(0, []),
    answerValue: new UntypedFormControl('1', [Validators.required]),
    colour: new UntypedFormControl(DEFAULT_ANSWER_COLOR, [Validators.required]),
    labelTitle: new UntypedFormControl('', [Validators.required, Validators.maxLength(255)]),
    labelDescription: new UntypedFormControl('', [Validators.maxLength(2000)])
  });

  if (possibleAnswer) {
    formGroup.controls.id.setValue(possibleAnswer.id, { emitEvent: false } );
    formGroup.controls.answerIndex.setValue(possibleAnswer.answerIndex, { emitEvent: false } );
    formGroup.controls.answerValue.setValue(possibleAnswer.answerValue, { emitEvent: false } );
    formGroup.controls.colour.setValue(possibleAnswer.colour, { emitEvent: false } );
    formGroup.controls.labelTitle.setValue(possibleAnswer.labelTitle, { emitEvent: false } );
    formGroup.controls.labelDescription.setValue(possibleAnswer.labelDescription, { emitEvent: false } );
  }

  return formGroup;
};

export const initFormArrayOptionItems = (possibleAnswers?: DynamicScalePossibleAnswer[]): UntypedFormArray => {
  const formArray = new UntypedFormArray([]);

  if (possibleAnswers && possibleAnswers.length > 0) {
    possibleAnswers.forEach((possibleAnswer: DynamicScalePossibleAnswer) => {
      formArray.push(initFormGroupOptionItem(possibleAnswer));
    });
  } else {
    DEFAULT_ANSWER_OPTIONS.forEach((possibleAnswer: DynamicScalePossibleAnswer) => {
      formArray.push(initFormGroupOptionItem(possibleAnswer));
    });
  }

  return formArray;
};

@Component({
  selector: 'app-schedule-cycle-add-question',
  templateUrl: './schedule-cycle-add-question.component.html',
  styleUrls: ['./schedule-cycle-add-question.component.scss']
})
export class ScheduleCycleAddQuestionComponent {
  public readonly CUSTOMIZABLE_QUESTION_TYPES = [ SurveyQuestionType.DYNAMIC_SCALE ];
  public readonly eSurveyQuestionType = SurveyQuestionType;
  public readonly eComponentAction = ComponentAction;
  public readonly eButtonType = ButtonType;
  public readonly eModalType = ModalType;

  @Input() modalActive: ModalType;
  @Input() addQuestionForm: UntypedFormGroup;

  @Output() onClickAction: EventEmitter<ComponentAction>;

  submitted: boolean;
  customizingScale: boolean;

  get canCustomizeQuestionType(): boolean {
    switch (this.modalActive) {
      case ModalType.ADD_QUESTION:
        return this.CUSTOMIZABLE_QUESTION_TYPES.includes(this.controlType.value);
      case ModalType.EDIT_QUESTION:
        if (this.addQuestionForm.controls.editingFrom.value === EditingFrom.FRANKLI_BANK) {
          return this.CUSTOMIZABLE_QUESTION_TYPES.includes(this.controlType.value);
        }

        return false;
    }
  }

  get controlText(): UntypedFormControl {
    return this.addQuestionForm.controls.text as UntypedFormControl;
  }

  get controlType(): UntypedFormControl {
    return this.addQuestionForm.controls.type as UntypedFormControl;
  }

  get controlIndex(): UntypedFormControl {
    return this.addQuestionForm.controls.index as UntypedFormControl;
  }

  get formAnswerOptions(): UntypedFormArray {
    return this.addQuestionForm.controls.options as UntypedFormArray;
  }

  constructor() {
    this.modalActive = null;
    this.customizingScale = false;
    this.onClickAction = new EventEmitter<ComponentAction>();
    this.addQuestionForm = initFormAddReviewQuestion();
  }


  navigatePrevious(): void {
    return this.doAction(ComponentAction.CANCEL);
  }

  navigateNext(event?: KeyboardEvent): void {
    if (event) {
      event.preventDefault();
    }

    this.submitted = true;

    const currentStepIsValid = this.checkIsCurrentStepValid();
    if(!currentStepIsValid) {
      return;
    }

    return this.doAction(ComponentAction.SUBMIT);
  }

  checkIsCurrentStepValid(): boolean {
    if (this.canCustomizeQuestionType) {
      return this.controlText.valid && this.controlType.valid && this.formAnswerOptions.valid;
    } else {
      return this.controlText.valid && this.controlType.valid;
    }
  }

  setQuestionType(type: SurveyQuestionType) {
    this.addQuestionForm.controls.type.setValue(type);
  }

  setModalType(type: ModalType): void {
    this.modalActive = type;
  }

  doAction(action: ComponentAction, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    this.onClickAction.emit(action);
  }

  startEditOptions(): void {
    if (!this.canCustomizeQuestionType) { return; }
    this.customizingScale = true;
  }

  leaveEditOptions(): void {
    this.customizingScale = false;
  }
}
